/**
 * AccountingGateContext
 * =====================
 * Backs the mandatory "Connect your accounting" onboarding gate.
 *
 * A real (non-demo) client cannot use the dashboards until at least one
 * accounting source (MYOB / Xero / QuickBooks) is connected — there is no
 * live data to show otherwise. Demo accounts and Corvana staff are exempt
 * (the backend decides exemption authoritatively).
 *
 * The gate FAILS OPEN on a network/transport error so a transient backend
 * blip can never lock a legitimate user out of their own app.
 */
import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from './AuthContext';
import { authFetch } from '../utils/authFetch';
import { API_URL } from '../config/apiConfig';

const AccountingGateContext = createContext({
  loading: true,
  allowed: true,
  connected: false,
  providers: {},
  isDemo: false,
  isOperator: false,
  recheck: async () => true,
});

export function AccountingGateProvider({ children }) {
  const { user } = useAuth();
  const [state, setState] = useState({
    loading: true,
    allowed: true,
    connected: false,
    providers: {},
    isDemo: false,
    isOperator: false,
  });

  const recheck = useCallback(async () => {
    try {
      const res = await authFetch(`${API_URL}/api/integrations/accounting/gate-status`);
      const data = await res.json();
      const setupLock = !!data.setup_lock;
      // Under an operator setup lock the client stays gated even if accounting
      // is connected, until Corvana finishes verifying their integrations.
      const allowed = setupLock ? false : !!(data.exempt || data.connected);
      setState({
        loading: false,
        allowed,
        connected: !!data.connected,
        providers: data.providers || {},
        isDemo: !!data.is_demo,
        isOperator: !!data.is_operator,
        setupLock,
        setup: data.setup || null,
      });
      return allowed;
    } catch (e) {
      // Fail open — never lock a user out on a transient error.
      setState((s) => ({ ...s, loading: false, allowed: true }));
      return true;
    }
  }, []);

  useEffect(() => {
    if (!user) {
      setState((s) => ({ ...s, loading: false, allowed: true }));
      return;
    }
    setState((s) => ({ ...s, loading: true }));
    recheck();
  }, [user?.id, user?.email, recheck]);

  return (
    <AccountingGateContext.Provider value={{ ...state, recheck }}>
      {children}
    </AccountingGateContext.Provider>
  );
}

export const useAccountingGate = () => useContext(AccountingGateContext);
